// How the Contacts page orders people (PL-156, 2026-09-23). Strength alone — how often and how recently you
// wrote — put colleagues, vendors and machine senders at the top of Don's list, and he filed nine tickets against
// rows that were right. The list now leads with the ties BD is for: customers, prospects, and the partners and
// referral sources who send work — then everyone else, then colleagues and vendors. Strength orders within a
// tier; the old view is one toggle away. A machine (role 'machine') is never a contact and is hidden by default.
import type { ContactView } from '../types/contacts'

export type ContactSort = 'matters' | 'strength'

/** Lower is nearer the top. A referral source ranks with the partners whatever their role says. */
export const ROLE_TIER: Record<string, number> = {
  customer: 0,
  prospect: 1,
  partner: 2,
  other: 3,
  competitor: 3,
  colleague: 4,
  vendor: 5,
  machine: 6,
}

export const TIER_WORDS: Record<number, string> = {
  0: 'Customers',
  1: 'Prospects',
  2: 'Partners and referral sources',
  3: 'Everyone else',
  4: 'Colleagues',
  5: 'Vendors',
  6: 'Machine mail',
}

export function isMachine(c: ContactView): boolean {
  return c.person.role === 'machine'
}

export function tierOf(c: ContactView, referralSource: boolean): number {
  const role = c.person.role ?? 'other'
  const tier = ROLE_TIER[role] ?? 3
  return referralSource && tier > 2 ? 2 : tier
}

function lastSeen(c: ContactView): number {
  const t = Date.parse(c.person.lastSeen ?? '')
  return Number.isNaN(t) ? 0 : t
}

/**
 * The rep's own vote comes before everything else (REQ-123): important (0) above unvoted (1) above less (2).
 * A vote never hides a person — "less" still sits in the list, at the bottom.
 */
export function voteTier(c: ContactView): number {
  const v = c.vote ?? 0
  return v > 0 ? 0 : v < 0 ? 2 : 1
}

/** The group for everyone whose own mail never says where they write from. */
export const LOCATION_UNKNOWN = 'Location unknown'

export interface LocationGroup { location: string; rows: ContactView[] }

/**
 * The list grouped by where each person writes from (REQ-126, owner 2026-10-01): the places with the most people
 * first, equal counts by name, and the unplaced last. The order inside a group is the order given — rank first,
 * then group, so the vote and the tier still lead within a place.
 */
export function groupByLocation(ranked: ContactView[]): LocationGroup[] {
  const by = new Map<string, ContactView[]>()
  for (const c of ranked) {
    const k = c.person.location?.trim() || LOCATION_UNKNOWN
    if (!by.has(k)) by.set(k, [])
    by.get(k)!.push(c)
  }
  return [...by.entries()].map(([location, rows]) => ({ location, rows })).sort((a, b) => {
    if (a.location === LOCATION_UNKNOWN) return 1
    if (b.location === LOCATION_UNKNOWN) return -1
    return b.rows.length - a.rows.length || a.location.localeCompare(b.location)
  })
}

/** A stable ordering: the vote first in either view, then by tier, strength and recency — or by strength and recency alone. */
export function rankContacts(list: ContactView[], sort: ContactSort, referralSources: Set<string>): ContactView[] {
  const strength = (c: ContactView) => c.person.relationshipStrength ?? 0
  return [...list].sort((a, b) => {
    const va = voteTier(a); const vb = voteTier(b)
    if (va !== vb) return va - vb
    if (sort === 'matters') {
      const ta = tierOf(a, referralSources.has(a.person.personId))
      const tb = tierOf(b, referralSources.has(b.person.personId))
      if (ta !== tb) return ta - tb
    }
    if (strength(a) !== strength(b)) return strength(b) - strength(a)
    return lastSeen(b) - lastSeen(a)
  })
}
